// compValueChange with server-side cascade resolution (MobileProxy request flag resolveCascade).
// Pure helpers without Vue/Quasar dependencies - used by Formular.vue and the node tests.
import {StaticData} from './StaticData.js'

/**
 * Sequence numbers per formular instance: discards outdated compValueChange responses when the user
 * changes fields faster than the server answers.
 *  - a response is outdated completely, if a newer request for the same trigger field was started
 *  - a single entry is outdated, if a newer request was started for the entry field itself (the user changed
 *    it in the meantime) or if the entry was derived from an outdated entry (cascadeSource chain)
 */
export function createCompValueSequencer() {
  let counter = 0
  const lastSeqByField = {}

  const names = triggers => (Array.isArray(triggers) ? triggers : [triggers])

  return {
    /** triggers: nativename of the changed field, or the trigger list of a follow-up request */
    start(triggers) {
      counter++
      for (const name of names(triggers)) lastSeqByField[name] = counter
      return counter
    },

    isNewest(seq, triggers) {
      return names(triggers).every(name => !(lastSeqByField[name] > seq))
    },

    filter(seq, triggers, entries) {
      const list = Array.isArray(entries) ? entries : []
      if (!this.isNewest(seq, triggers)) return []
      const dropped = new Set()
      return list.filter(entry => {
        if (!entry) return false
        const outdated = lastSeqByField[entry.key] > seq || (entry.cascadeSource && dropped.has(entry.cascadeSource))
        if (outdated) dropped.add(entry.key)
        return !outdated
      })
    }
  }
}

/** true, if the server resolved the cascade of this entry (no further compValueChange for its effects) */
export function isCascadeResolved(entry) {
  return !!entry && entry.cascadeResolved === true
}

/**
 * sourceChangedNativename used when applying an entry: the field whose change caused the evaluation of the
 * entry on the server; legacy responses (old MobileProxy) keep the changed field.
 */
export function entrySource(entry, changedNativename) {
  return isCascadeResolved(entry) && entry.cascadeSource ? entry.cascadeSource : changedNativename
}

/**
 * instant refresh fields the client currently does not cascade from: field components only emit onChange
 * for instantRefresh fields with showThroughDependency === true or a master mode other than VISIBLE.
 */
export function getCascadeExcludedFields(formularList) {
  const result = []
  for (const field of flatFields(formularList)) {
    if (field.instantRefresh && !cascadesOnClient(field)) result.push(field.nativename)
  }
  return result
}

/** same condition as Formular.onFieldValueChange: the client cascades from this field */
function cascadesOnClient(field) {
  return field.showThroughDependency === true || field.masterPropertyMode !== StaticData.FORM_MASTERPROPERTY_MODES.VISIBLE
}

function flatFields(formularList) {
  const result = []
  for (const formular of formularList || []) {
    for (const field of formular?.formularFields?.formularFlatList || []) {
      if (field) result.push(field)
    }
  }
  return result
}

function normalizedValue(value) {
  return value === undefined || value === null ? '' : String(value)
}

/** values of the instant refresh fields before a cascadeResolved response is applied */
export function snapshotCascadeValues(formularList) {
  const result = {}
  for (const field of flatFields(formularList)) {
    if (field.instantRefresh) result[field.nativename] = normalizedValue(field.value)
  }
  return result
}

/**
 * Follow-up triggers after applying a cascadeResolved response (incl. hideOrDisableDependentFields):
 * instant refresh fields the server did not cascade from (cascadeExcludedFields of the request), that the
 * client now cascades from (became visible through a master property) and whose value changed while the
 * response was applied. Form order. Fields that stay hidden or keep their value start no follow-up.
 */
export function collectCascadeFollowUpTriggers(formularList, excludedFields, valuesBefore) {
  const excluded = new Set(excludedFields || [])
  const before = valuesBefore || {}
  const result = []
  for (const field of flatFields(formularList)) {
    if (!field.instantRefresh || !excluded.has(field.nativename) || !cascadesOnClient(field)) continue
    if (normalizedValue(before[field.nativename]) === normalizedValue(field.value)) continue
    if (!result.includes(field.nativename)) result.push(field.nativename)
  }
  return result
}

/** follow-up requests after the first cascade response at most (fields that became visible meanwhile) */
export const MAX_CASCADE_FOLLOW_UP_ROUNDS = 3

/**
 * compValueChange with server-side cascade incl. follow-up rounds:
 *  1. send(null, excluded): request for the changed field
 *  2. apply(entries): response applied (field refresh + master logic hideOrDisableDependentFields)
 *  3. fields excluded in that request, that became visible and changed value -> ONE bundled follow-up
 *     send(triggers, excluded) (MobileProxy cascadeTriggerFields); repeated up to maxFollowUpRounds.
 * Stops when send returns no list (outdated / discarded), on a legacy response without cascadeResolved
 * (the client side cascade of the field components runs instead) or when no new trigger shows up.
 *
 * @param getFormularList () => current formularList (fields are mutated while applying)
 * @param send (triggers|null, excludedFields) => Promise<entries[]|null>
 * @param apply (entries) => Promise|void
 * @returns {Promise<{followUps: number, limitReached?: boolean, pending?: string[], discarded?: boolean}>}
 */
export async function runCompValueCascade({getFormularList, send, apply, maxFollowUpRounds = MAX_CASCADE_FOLLOW_UP_ROUNDS}) {
  let triggers = null
  let followUps = 0
  for (;;) {
    const excluded = getCascadeExcludedFields(getFormularList())
    const entries = await send(triggers, excluded)
    if (!Array.isArray(entries)) return {followUps, discarded: true}

    const valuesBefore = snapshotCascadeValues(getFormularList())
    await apply(entries)
    if (!entries.some(isCascadeResolved)) return {followUps}

    const next = collectCascadeFollowUpTriggers(getFormularList(), excluded, valuesBefore)
    if (next.length === 0) return {followUps}
    if (followUps >= maxFollowUpRounds) return {followUps, limitReached: true, pending: next}
    followUps++
    triggers = next
  }
}
